import LiquidText, { liquidTone } from '../components/LiquidText';
import { LiquidDialog } from '../components/LiquidUI';
import { OrcamentoIcon } from '../components/OrcamentoUI';
import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import ConsultaTrabalhadores from './ConsultaTrabalhadores';
import NaoConformidades from './NaoConformidades';
import Instalacoes from './Instalacoes';
import PainelPrazos from './PainelPrazos';
import MatrizDocumentos from './MatrizDocumentos';
import ProcedimentoLink from '../components/ProcedimentoLink';
import ObjetivosIndicadores from './ObjetivosIndicadores';
import RiscosOportunidades from './RiscosOportunidades';
import Hira from './Hira';
import Acidentes from './Acidentes';
import Emergencia from './Emergencia';
import Epis from './Epis';
import RequisitosLegais from './RequisitosLegais';
import Governacao from './Governacao';
import Subcontratados from './Subcontratados';
import AcordosFornecedores from './AcordosFornecedores';
import Formacao from './Formacao';
import Rmm from './Rmm';
import DocumentosCertificacao from './DocumentosCertificacao';
import Relatorios from './Relatorios';
import VerificacaoObras from './VerificacaoObras';
import ProcuraSgi from '../components/ProcuraSgi';
import PontosAuditora from './PontosAuditora';
import { usePrazosSgi } from '../context/PrazosSgiContext';

/* ============================================================
   SISTEMA INTEGRADO (SGI)
   Fase 1 — Avaliação de Desempenho de Fornecedores (P_AF_01 / RG.11)
   ============================================================ */

// Separadores agrupados por área (como as pastas do SGI). Cada área abre a
// sua 1.ª sub-secção; a 2.ª linha mostra os separadores da área ativa.
const AREAS = [
    {
        key: 'partes', label: '🤝 Partes Interessadas', tabs: [
            ['fornecedores', 'Avaliação de Fornecedores'],
            ['acordos', '🔒 Confidencialidade\ne RGPD'],
            ['satisfacao', 'Satisfação do Cliente'],
            ['consultas', 'Consulta aos Trabalhadores'],
            ['subcontratados', '👷 Subcontratados'],
            ['validades_partes', '⏰ Validades'],
        ],
    },
    {
        key: 'sst', label: '🦺 Segurança (SST)', tabs: [
            ['hira', '🦺 HIRA'],
            ['acidentes', '🚑 Acidentes, Incidentes\ne Comunicações'],
            ['emergencia', '🚨 Emergência'],
            ['epis', '🧤 EPIs'],
            ['formacao', '🎓 Formação'],
            ['instalacoes', 'Instalações e Equipamentos'],
            ['verifobras', 'Verificação\nde Obras'],
            ['rmm', 'Recursos\nMonitorização\nMedição'],
            ['validades_sst', '⏰ Validades'],
        ],
    },
    {
        key: 'sistema', label: '📋 Sistema & Melhoria', tabs: [
            ['auditora', '📋 Lista da\nAuditora'],
            ['documentos', '📄 Documentos'],
            ['objetivos', '📈 Objetivos'],
            ['riscos', '⚖️ Riscos'],
            ['nc', 'Não Conformidades'],
            ['reqlegais', '📜 Req. Legais'],
            ['governacao', '🏛️ Governação'],
            ['validades_sistema', '⏰ Validades'],
        ],
    },
];

// Critérios de avaliação (RG.11). Cada critério pontua 1-3.
const CRITERIOS = [
    {
        key: 'crit_pagamento', label: 'Condições de Pagamento',
        op: { 1: 'Pronto pagamento', 2: 'Até 30 dias', 3: '> 30 dias' },
    },
    {
        key: 'crit_qualidade', label: 'Qualidade do produto / serviço',
        op: {
            1: 'Não cumpre frequentemente os requisitos / não disponibiliza informação documentada exigida (incl. SST)',
            2: 'Cumpre parcialmente / por vezes não disponibiliza informação documentada exigida (incl. SST)',
            3: 'Cumpre os requisitos (sem reclamações/devoluções) e disponibiliza informação documentada (incl. SST)',
        },
    },
    {
        key: 'crit_prazos', label: 'Prazos',
        op: {
            1: 'Atrasos na entrega/serviço COM consequências para a conformidade do serviço/sistema',
            2: 'Atrasos na entrega/serviço SEM consequências para a conformidade do serviço/sistema',
            3: 'Não há registo de atrasos na entrega/serviços',
        },
    },
    {
        key: 'crit_flexibilidade', label: 'Flexibilidade',
        op: {
            1: 'Dificuldade na adaptação às necessidades do cliente',
            2: 'Adapta-se frequentemente às necessidades do cliente',
            3: 'Adapta-se às necessidades do cliente',
        },
    },
    {
        key: 'crit_nc_sst', label: 'NC / Reclamações / Incidentes SST',
        op: {
            1: 'Resposta morosa a NC/reclamações; incidente SST materializado como AT',
            2: 'Resposta adequada a NC/reclamações; incidente SST quase-AT',
            3: 'Não há registo de reclamações/NC ou incidentes SST',
        },
    },
    {
        key: 'crit_desempenho', label: 'Desempenho geral',
        op: { 1: 'Abaixo do esperado', 2: 'Cumpre o esperado', 3: 'Acima do esperado' },
    },
];

const eur = (v) => (parseFloat(v) || 0).toLocaleString('pt-PT', { style: 'currency', currency: 'EUR' });

const nivelInfo = (nivel) => {
    switch (nivel) {
        case 'A': return { label: 'A — Qualificado', bg: '#dcfce7', fg: '#166534' };
        case 'B': return { label: 'B — Qualificado c/ reservas', bg: '#fef9c3', fg: '#854d0e' };
        case 'C': return { label: 'C — Não qualificado', bg: '#fee2e2', fg: '#991b1b' };
        default: return { label: '—', bg: '#f1f5f9', fg: '#64748b' };
    }
};

const motivoInfo = (motivo) => {
    switch (motivo) {
        case 'ambos': return { label: 'Valor + Crítico', bg: '#e0e7ff', fg: '#3730a3' };
        case 'critico': return { label: 'Crítico p/ sistema', bg: '#fae8ff', fg: '#86198f' };
        case 'valor': return { label: 'Acima do limite', bg: '#e0f2fe', fg: '#075985' };
        default: return { label: '—', bg: '#f1f5f9', fg: '#64748b' };
    }
};

function calcIqf(form) {
    const vals = CRITERIOS.map(c => parseInt(form[c.key], 10)).filter(v => v >= 1 && v <= 3);
    if (vals.length < CRITERIOS.length) return { iqf: null, nivel: null };
    const iqf = Math.round((vals.reduce((a, b) => a + b, 0) / vals.length) * 100) / 100;
    let nivel = iqf >= 2 ? 'A' : (iqf > 1.5 ? 'B' : 'C');
    return { iqf, nivel };
}

/* ---------- Modal de avaliação ---------- */
function ModalAvaliacao({ candidato, ano, onClose, onSaved }) {
    const init = {};
    CRITERIOS.forEach(c => { init[c.key] = candidato[c.key] || ''; });
    const [form, setForm] = useState({
        ...init,
        observacoes: candidato.observacoes || '',
        data_avaliacao: candidato.data_avaliacao
            ? new Date(candidato.data_avaliacao).toISOString().slice(0, 10)
            : new Date().toISOString().slice(0, 10),
    });
    const [saving, setSaving] = useState(false);
    const [err, setErr] = useState('');
    const { iqf, nivel } = calcIqf(form);
    const ni = nivelInfo(nivel);

    const save = async () => {
        if (CRITERIOS.some(c => !form[c.key])) { setErr('Pontue todos os 6 critérios.'); return; }
        setSaving(true); setErr('');
        try {
            await api.post('/sistema-integrado/avaliacao-fornecedores/avaliacoes', {
                fornecedor_id: candidato.id,
                ano,
                data_avaliacao: form.data_avaliacao,
                observacoes: form.observacoes,
                ...CRITERIOS.reduce((acc, c) => ({ ...acc, [c.key]: form[c.key] }), {}),
            });
            onSaved();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao guardar avaliação');
        } finally { setSaving(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={modal} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
                    <div>
                        <h3 style={{ margin: 0 }}>Avaliação de Fornecedor — {ano}</h3>
                        <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 2 }}>
                            {candidato.nome}{candidato.nif ? ` · ${candidato.nif}` : ''}
                        </div>
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 14, margin: '16px 0' }}>
                    {CRITERIOS.map(c => (
                        <div key={c.key}>
                            <label style={{ fontWeight: 600, fontSize: '0.85rem', display: 'block', marginBottom: 5 }}><LiquidText>{c.label}</LiquidText></label>
                            <div style={{ display: 'flex', gap: 8 }}>
                                {[1, 2, 3].map(n => {
                                    const sel = String(form[c.key]) === String(n);
                                    return (
                                        <button data-liquid-choice="true" aria-pressed={sel} key={n} type="button"
                                            onClick={() => setForm(p => ({ ...p, [c.key]: n }))}
                                            title={c.op[n]}
                                            style={{
                                                flex: 1, padding: '8px 6px', borderRadius: 8, cursor: 'pointer',
                                                border: sel ? '1px solid #3182ce' : '1px solid #e2e8f0',
                                                background: sel ? '#ebf5ff' : '#fff',
                                                color: sel ? '#1e40af' : '#475569',
                                                fontSize: '0.74rem', textAlign: 'left', lineHeight: 1.25,
                                            }}>
                                            <strong style={{ display: 'block', fontSize: '0.9rem' }}>{n}</strong>
                                            {c.op[n]}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    ))}
                </div>

                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center', marginBottom: 14 }}>
                    <div>
                        <label data-liquid-label="true" style={lbl}>Data da avaliação</label>
                        <input type="date" value={form.data_avaliacao}
                            onChange={e => setForm(p => ({ ...p, data_avaliacao: e.target.value }))} style={inp} />
                    </div>
                    <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
                        <div style={{ fontSize: '0.78rem', color: '#64748b' }}>IQF (média)</div>
                        <div style={{ fontSize: '1.6rem', fontWeight: 800, lineHeight: 1 }}>{iqf !== null ? iqf.toFixed(2) : '—'}</div>
                    </div>
                    <span data-liquid-tone={liquidTone(ni.bg)} data-liquid-badge="true" style={{ ...badge, background: ni.bg, color: ni.fg, fontSize: '0.85rem', padding: '6px 12px' }}><LiquidText>{ni.label}</LiquidText></span>
                </div>

                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Observações</label>
                    <textarea value={form.observacoes} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }}
                        onChange={e => setForm(p => ({ ...p, observacoes: e.target.value }))} />
                </div>

                {err && <div style={{ color: '#e53e3e', marginBottom: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={save} disabled={saving} style={{ ...btnPri, opacity: saving ? 0.6 : 1 }}>
                        {saving ? 'A guardar…' : 'Guardar avaliação'}
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Modal de envio de email ---------- */
function ModalEmail({ candidato, onClose, onSent }) {
    const ni = nivelInfo(candidato.nivel);
    const [destinatario, setDestinatario] = useState(candidato.email || '');
    const [mensagem, setMensagem] = useState('');
    const [sending, setSending] = useState(false);
    const [err, setErr] = useState('');

    const enviar = async () => {
        if (!destinatario.trim()) { setErr('Indique o email do destinatário.'); return; }
        setSending(true); setErr('');
        try {
            await api.post(`/sistema-integrado/avaliacao-fornecedores/avaliacoes/${candidato.avaliacao_id}/enviar-email`, {
                destinatario: destinatario.trim(),
                mensagem,
            });
            onSent();
            onClose();
        } catch (e) {
            setErr(e.response?.data?.error || 'Erro ao enviar email');
        } finally { setSending(false); }
    };

    return (
        <LiquidDialog data-sgi-dialog="true" onClose={onClose} style={overlay} onClick={onClose}>
            <div style={{ ...modal, maxWidth: 520 }} onClick={e => e.stopPropagation()}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
                    <div>
                        <h3 style={{ margin: 0 }}>Comunicar resultado</h3>
                        <div style={{ color: '#64748b', fontSize: '0.9rem', marginTop: 2 }}>{candidato.nome}</div>
                    </div>
                    <button data-liquid-close="true" aria-label="Fechar" onClick={onClose} style={closeBtn}><LiquidText>✕</LiquidText></button>
                </div>

                <div style={{ display: 'flex', gap: 8, alignItems: 'center', margin: '12px 0' }}>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>Será comunicado:</span>
                    <span data-liquid-tone={liquidTone(ni.bg)} data-liquid-badge="true" style={{ ...badge, background: ni.bg, color: ni.fg }}><LiquidText>{ni.label}</LiquidText></span>
                    <span style={{ color: '#64748b', fontSize: '0.85rem' }}>· IQF {candidato.iqf != null ? Number(candidato.iqf).toFixed(2) : '—'}</span>
                </div>

                {candidato.email_enviado_em && (
                    <div data-liquid-notice="true" data-liquid-tone={liquidTone('#f0fdf4')} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#166534', padding: '8px 12px', borderRadius: 8, fontSize: '0.82rem', marginBottom: 12 }}>
                        Já comunicado em {new Date(candidato.email_enviado_em).toLocaleString('pt-PT')}{candidato.email_destinatario ? ` para ${candidato.email_destinatario}` : ''}. Pode reenviar.
                    </div>
                )}

                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Destinatário</label>
                    <input type="email" value={destinatario} onChange={e => setDestinatario(e.target.value)}
                        placeholder="email@fornecedor.pt" style={{ ...inp, width: '100%' }} />
                </div>
                <div style={{ marginBottom: 12 }}>
                    <label data-liquid-label="true" style={lbl}>Mensagem adicional (opcional)</label>
                    <textarea value={mensagem} rows={3} style={{ ...inp, width: '100%', resize: 'vertical' }}
                        onChange={e => setMensagem(e.target.value)} placeholder="Texto livre incluído no email…" />
                </div>

                {err && <div style={{ color: '#e53e3e', marginBottom: 10, fontSize: '0.85rem' }}>{err}</div>}

                <div data-liquid-actions="true" style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <button data-liquid-secondary="true" onClick={onClose} style={btnSec}>Cancelar</button>
                    <button data-liquid-primary="true" onClick={enviar} disabled={sending} style={{ ...btnPri, opacity: sending ? 0.6 : 1 }}>
                        <LiquidText>{sending ? 'A enviar…' : '✉ Enviar email'}</LiquidText>
                    </button>
                </div>
            </div>
        </LiquidDialog>
    );
}

/* ---------- Número de prazos num botão ----------
   Vermelho com os atrasados; sem atrasados, âmbar com os que vencem dentro do
   aviso prévio. Cada área e cada separador leva o seu, para se ver onde está o
   atraso sem abrir separador a separador — era por não se ver que os prazos
   ficavam órfãos. Só o número: o que é diz o título, ao passar o rato. */
const semIcone = (s) => String(s || '').replace(/\n/g, ' ').replace(/^[^A-Za-zÀ-ÿ]+/, '');

/* O separador de Validades de cada área leva os dois números lado a lado:
   vermelho o que já venceu, amarelo o que vence dentro do aviso. É o único sítio
   da barra com números — os separadores de origem ficam limpos. */
const AREA_DO_PAINEL = { validades_partes: 'partes', validades_sst: 'sst', validades_sistema: 'sistema' };

function NumerosValidades({ contagem, aviso, area }) {
    const atrasados = contagem?.atrasado || 0;
    const aVencer = contagem?.a_vencer || 0;
    if (!atrasados && !aVencer) return null;
    const pilula = (n, fundo, cor, titulo) => (
        <span data-liquid-tone={liquidTone(fundo)} data-liquid-badge="true" title={titulo} aria-label={titulo} style={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, boxSizing: 'border-box',
            background: fundo, color: cor, fontSize: 10.5, fontWeight: 800, lineHeight: 1,
            whiteSpace: 'nowrap', boxShadow: '0 0 0 2px #fff',
        }}>
            {n}
        </span>
    );
    return (
        <span className="sgi-validity-counters" style={{ position: 'absolute', top: -16, right: -6, zIndex: 1, display: 'inline-flex', gap: 3 }}>
            {atrasados > 0 && pilula(atrasados, '#d03b3b', '#fff',
                `${atrasados} ${atrasados === 1 ? 'situação vencida' : 'situações vencidas'} em ${area}`)}
            {aVencer > 0 && pilula(aVencer, '#fab219', '#3b2a00',
                `${aVencer} ${aVencer === 1 ? 'situação vence' : 'situações vencem'} nos próximos ${aviso} dias em ${area}`)}
        </span>
    );
}

function NumeroPrazos({ contagem, aviso, onde }) {
    const atrasados = contagem?.atrasado || 0;
    const aVencer = contagem?.a_vencer || 0;
    if (!atrasados && !aVencer) return null;
    const emAtraso = atrasados > 0;
    const titulo = emAtraso
        ? `${atrasados} ${atrasados === 1 ? 'prazo em atraso' : 'prazos em atraso'} ${onde}`
            + (aVencer ? ` e ${aVencer} a vencer nos próximos ${aviso} dias` : '')
        : `${aVencer} ${aVencer === 1 ? 'prazo a vencer' : 'prazos a vencer'} nos próximos ${aviso} dias ${onde}`;
    return (
        <span className="sgi-counter" data-liquid-tone={emAtraso ? 'danger' : 'warning'} title={titulo} aria-label={titulo} style={{
            position: 'absolute', top: -7, right: -6, zIndex: 1,
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, boxSizing: 'border-box',
            background: emAtraso ? '#d03b3b' : '#fab219', color: emAtraso ? '#fff' : '#3b2a00',
            fontSize: 10.5, fontWeight: 800, lineHeight: 1, whiteSpace: 'nowrap',
            boxShadow: '0 0 0 2px #fff',
        }}>
            {emAtraso ? atrasados : aVencer}
        </span>
    );
}

/* ---------- Página ---------- */
const SistemaIntegrado = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const isAdmin = user?.role === 'admin';
    const anoAtual = new Date().getFullYear();

    const [secao, setSecao] = useState(
        () => new URLSearchParams(window.location.search).get('secao') || 'fornecedores');
    const [procurar, setProcurar] = useState(false);

    // Os números de prazos das áreas e dos separadores (🔔 Prazos). Voltam a
    // ser pedidos a cada mudança de separador: quem acabou de registar a
    // medição ou de fechar a NC vê logo o número descer.
    const { resumo: prazos, recarregar: recarregarPrazos, ativo: prazosAtivos } = usePrazosSgi();
    useEffect(() => { if (prazosAtivos) recarregarPrazos(); }, [secao, prazosAtivos, recarregarPrazos]);

    /* Ctrl+L abre a procura de qualquer ponto do Sistema Integrado. Durante uma
       auditoria a pergunta vem depressa e não há tempo para procurar o botão. */
    useEffect(() => {
        const atalho = (e) => {
            if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'l') {
                e.preventDefault();
                setProcurar(true);
            }
        };
        window.addEventListener('keydown', atalho);
        return () => window.removeEventListener('keydown', atalho);
    }, []);
    // Sub-separadores dentro de "Instalações e Equipamentos" — os três blocos
    // eram uma página só, comprida, com as instalações no fim.
    const [subIE, setSubIE] = useState('documentos');
    const [ano, setAno] = useState(anoAtual);
    const [limite, setLimite] = useState(20000);
    const [candidatos, setCandidatos] = useState([]);
    const [loading, setLoading] = useState(true);
    const [erro, setErro] = useState(null);
    const [editando, setEditando] = useState(null);
    const [emailModal, setEmailModal] = useState(null);
    const [limiteEdit, setLimiteEdit] = useState(false);
    const [limiteVal, setLimiteVal] = useState('');

    // "Abrir" no painel dos prazos: vai ao separador de onde o prazo vem. Os
    // registos de segurança das instalações estão no terceiro bloco desse
    // separador, e é esse que se abre.
    const abrirSeparador = (k) => {
        setSecao(k);
        if (k === 'instalacoes') setSubIE('instalacoes');
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const carregar = useCallback(async () => {
        setLoading(true); setErro(null);
        try {
            const res = await api.get('/sistema-integrado/avaliacao-fornecedores/candidatos', { params: { ano } });
            setCandidatos(res.data.candidatos || []);
            setLimite(res.data.limite ?? 20000);
        } catch (err) {
            console.error('Erro ao carregar candidatos:', err);
            setErro('Não foi possível carregar os fornecedores a avaliar.');
        } finally { setLoading(false); }
    }, [ano]);

    useEffect(() => { carregar(); }, [carregar]);

    const [enviandoTodos, setEnviandoTodos] = useState(false);

    const enviarTodos = async () => {
        const avaliados = candidatos.filter(c => c.avaliacao_id);
        if (avaliados.length === 0) { alert('Não há avaliações para comunicar.'); return; }
        if (!window.confirm(
            `Comunicar o resultado por email a ${avaliados.length} fornecedor(es) avaliado(s) em ${ano}?\n\n` +
            `Fornecedores sem email serão ignorados.`
        )) return;
        setEnviandoTodos(true);
        try {
            const res = await api.post('/sistema-integrado/avaliacao-fornecedores/enviar-todos', { ano });
            const { enviados, sem_email = [], erros = [] } = res.data;
            let msg = `${enviados} email(s) enviado(s).`;
            if (sem_email.length) msg += `\n\nSem email (ignorados): ${sem_email.join(', ')}`;
            if (erros.length) msg += `\n\nErros: ${erros.join('; ')}`;
            alert(msg);
            carregar();
        } catch (err) {
            alert(err.response?.data?.error || 'Erro ao enviar emails');
        } finally { setEnviandoTodos(false); }
    };

    const guardarLimite = async () => {
        const v = parseFloat(String(limiteVal).replace(',', '.'));
        if (!Number.isFinite(v) || v < 0) { alert('Valor inválido'); return; }
        try {
            await api.put('/sistema-integrado/config', { aval_forn_limite_valor: v });
            setLimite(v); setLimiteEdit(false);
            carregar();
        } catch (err) {
            alert(err.response?.data?.error || 'Erro ao guardar limite');
        }
    };

    const anos = [];
    for (let a = anoAtual + 1; a >= anoAtual - 6; a--) anos.push(a);

    const pendentes = candidatos.filter(c => !c.avaliacao_id).length;

    return (
        <div className="sgi-page" style={{ padding: '24px 28px' }}>
            <div className="sgi-page-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 8 }}>
                        <OrcamentoIcon name="shield" fallback="🎯" /> Sistema Integrado
                    </h1>
                    <p style={{ margin: '4px 0 0', color: '#718096', fontSize: '0.9rem' }}>
                        Gestão da certificação da empresa.
                    </p>
                </div>
                {secao === 'fornecedores' && (
                    <div data-liquid-actions="true" style={{ display: 'flex', gap: 8 }}>
                        <button data-liquid-primary="true" onClick={enviarTodos} disabled={enviandoTodos || candidatos.filter(c => c.avaliacao_id).length === 0}
                            style={{ ...btnPri, opacity: (enviandoTodos || candidatos.filter(c => c.avaliacao_id).length === 0) ? 0.5 : 1 }}
                            title="Comunicar o resultado por email a todos os fornecedores avaliados">
                            <LiquidText>{enviandoTodos ? 'A enviar…' : '✉ Enviar a todos'}</LiquidText>
                        </button>
                        <button data-liquid-secondary="true" onClick={carregar} style={btnSec}><LiquidText>↻ Atualizar</LiquidText></button>
                    </div>
                )}
            </div>

            <div className="sgi-content">
            {/* Navegação por áreas (2 níveis): área → separadores da área */}
            <div className="sgi-area-tabs" role="group" aria-label="Áreas do Sistema Integrado">
                {AREAS.map(a => {
                    const ativa = a.tabs.some(([k]) => k === secao);
                    return (
                        <button key={a.key} aria-pressed={ativa} onClick={() => setSecao(a.tabs[0][0])}
                            style={{
                                position: 'relative',
                                padding: '8px 16px', borderRadius: 10, cursor: 'pointer', fontSize: '0.88rem', fontWeight: 700,
                                border: `1px solid ${ativa ? '#1e293b' : '#cbd5e1'}`,
                                background: ativa ? '#1e293b' : '#fff',
                                color: ativa ? '#fff' : '#475569',
                            }}>
                            <OrcamentoIcon name={{partes:'users',sst:'shield',sistema:'layers'}[a.key]} fallback="" /> {semIcone(a.label)}
                            <NumeroPrazos contagem={prazos?.por_area?.[a.key]} aviso={prazos?.aviso} onde={`em ${semIcone(a.label)}`} />
                        </button>
                    );
                })}
                {/* A lupa: procura em todo o SGI, tolerante a acentos e gralhas.
                    Pensada para responder no momento ao que a auditora pedir. */}
                <button onClick={() => setProcurar(true)} title="Procurar em todo o Sistema Integrado"
                    style={{
                        marginLeft: 'auto', padding: '8px 18px', borderRadius: 10, cursor: 'pointer',
                        fontSize: '0.9rem', fontWeight: 700, border: '1px solid #3182ce',
                        background: '#eff6ff', color: '#1d4ed8',
                    }}>
                    <OrcamentoIcon name="search" fallback="🔍" /> Procurar <span style={{ opacity: 0.55, fontWeight: 500, fontSize: '0.78rem' }}>Ctrl+L</span>
                </button>
            </div>
            <div className="shared-subnav sgi-section-tabs" style={{ margin: '0 0 14px' }}>
                {(AREAS.find(a => a.tabs.some(([k]) => k === secao)) || AREAS[0]).tabs.map(([k, label]) => (
                    // Etiquetas com \n quebram em várias linhas para não alargar
                    // a barra; as restantes ficam exatamente como estavam.
                    // O position:relative é só a âncora do número de prazos.
                    <button key={k} className={`shared-subnav-btn ${secao === k ? 'active' : ''}`}
                        style={label.includes('\n')
                            ? { position: 'relative', whiteSpace: 'pre-line', lineHeight: 1.15, textAlign: 'center' }
                            : { position: 'relative' }}
                        onClick={() => setSecao(k)}>
                        {semIcone(label).replace(/\n/g, ' ')}
                        {/* Só o separador de Validades da área leva números. Um
                            número em cada separador enchia a barra de vermelhos e
                            obrigava a lê-la toda para saber onde era o problema. */}
                        {AREA_DO_PAINEL[k] && (
                            <NumerosValidades contagem={prazos?.por_area?.[AREA_DO_PAINEL[k]]} aviso={prazos?.aviso}
                                area={semIcone((AREAS.find(a => a.key === AREA_DO_PAINEL[k]) || {}).label || '')} />
                        )}
                    </button>
                ))}
            </div>

            {['satisfacao', 'nc', 'acidentes', 'documentos'].includes(secao) && (
                <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
                    <ProcedimentoLink key={secao}
                        codigo={secao === 'satisfacao' ? 'PR.CM.01' : secao === 'documentos' ? 'PR.GSI.01' : 'PR.GSI.03'}
                        titulo={secao === 'satisfacao' ? 'Procedimento Comercial' : secao === 'documentos' ? 'Gestão da Informação Documentada' : 'Procedimento de Incidentes e Não Conformidades'} />
                </div>
            )}

            {secao === 'auditora' && <PontosAuditora />}

            {secao === 'documentos' && <MatrizDocumentos />}

            {secao === 'objetivos' && <ObjetivosIndicadores />}

            {secao === 'riscos' && <RiscosOportunidades />}

            {secao === 'hira' && <Hira />}

            {secao === 'acidentes' && <Acidentes />}

            {secao === 'emergencia' && <Emergencia />}

            {secao === 'epis' && <Epis />}

            {secao === 'reqlegais' && <RequisitosLegais />}

            {secao === 'formacao' && <Formacao />}

            {secao === 'verifobras' && <VerificacaoObras />}

            {secao === 'rmm' && <Rmm />}

            {secao === 'governacao' && <Governacao />}

            {secao === 'subcontratados' && <Subcontratados />}

            {secao === 'acordos' && <AcordosFornecedores />}

            {AREA_DO_PAINEL[secao] && (
                <PainelPrazos areas={AREAS} areaFixa={AREA_DO_PAINEL[secao]} onAbrir={abrirSeparador} />
            )}


            {procurar && <ProcuraSgi onFechar={() => setProcurar(false)} />}

            {secao === 'consultas' && <ConsultaTrabalhadores />}

            {secao === 'satisfacao' && <ConsultaTrabalhadores alvo="clientes" />}

            {secao === 'nc' && <NaoConformidades />}

            {secao === 'instalacoes' && (
                <>
                    <div data-liquid-notice="true" data-liquid-tone={liquidTone('#eef2ff')} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, background: '#eef2ff', border: '1px solid #c7d2fe', borderRadius: 10, padding: '12px 16px', marginBottom: 16 }}>
                        <div>
                            <strong><LiquidText>🛠️ Equipamentos & Ferramentas</LiquidText></strong>
                            <div style={{ fontSize: '0.82rem', color: '#4338ca' }}>Fichas de equipamentos, verificações DL50 e QR codes.</div>
                        </div>
                        <button data-liquid-primary="true" onClick={() => navigate('/equipamentos')} style={btnPri}>Gerir equipamentos →</button>
                    </div>

                    {/* Três ícones em vez de uma barra de texto corrido: os nomes
                        completos não cabiam sem pesar o ecrã. A descrição de cada um
                        fica no próprio botão, o que dispensa a linha de texto abaixo. */}
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10, marginBottom: 18 }}>
                        {[
                            ['documentos', '📁', 'Documentos', 'Equipamentos e ferramentas, em PDF e por ano'],
                            ['verificacoes', '📋', 'Verificações', 'DL 50/2005 e andaimes, com o resultado de cada uma'],
                            ['instalacoes', '🏢', 'Instalações', 'Extintores, CDI, iluminação de emergência e porta corta-fogo'],
                        ].map(([k, icone, titulo, desc]) => {
                            const ativo = subIE === k;
                            return (
                                <button className="sgi-section-card" aria-pressed={ativo} key={k} onClick={() => setSubIE(k)} title={desc}
                                    style={{
                                        display: 'flex', alignItems: 'center', gap: 12, textAlign: 'left',
                                        padding: '12px 14px', borderRadius: 12, cursor: 'pointer',
                                        border: `1px solid ${ativo ? '#3182ce' : '#e2e8f0'}`,
                                        background: ativo ? '#eff6ff' : '#fff',
                                        boxShadow: ativo ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
                                    }}>
                                    <span style={{ fontSize: '1.7rem', lineHeight: 1 }}><LiquidText>{icone}</LiquidText></span>
                                    <span style={{ minWidth: 0 }}>
                                        <span style={{ display: 'block', fontWeight: 700, fontSize: '0.94rem', color: ativo ? '#1e40af' : '#1e293b' }}>
                                            <LiquidText>{titulo}</LiquidText>
                                        </span>
                                        <span style={{ display: 'block', fontSize: '0.74rem', color: '#94a3b8', marginTop: 2 }}>
                                            {desc}
                                        </span>
                                    </span>
                                </button>
                            );
                        })}
                    </div>

                    {/* A documentação em PDF aparece aqui e na página Equipamentos —
                        é o mesmo componente sobre a mesma tabela, uma porta a mais e
                        não uma segunda cópia. */}
                    {subIE === 'documentos' && <DocumentosCertificacao />}

                    {/* A mesma lista da página Relatórios, com o cabeçalho escondido.
                        O documento mostra uma verificação de cada vez; é o registo que
                        responde a "quantas houve em 2025 e alguma deu não conforme". */}
                    {subIE === 'verificacoes' && <Relatorios embutido />}

                    {subIE === 'instalacoes' && <Instalacoes />}
                </>
            )}

            {secao === 'fornecedores' && (
            <>
            {/* Barra de controlo */}
            <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', marginBottom: 16 }}>
                <div>
                    <label data-liquid-label="true" style={lbl}>Ano</label>
                    <select value={ano} onChange={e => setAno(parseInt(e.target.value, 10))} style={inp}>
                        {anos.map(a => <option key={a} value={a}>{a}</option>)}
                    </select>
                </div>
                <div>
                    <label data-liquid-label="true" style={lbl}>Limite de faturação anual</label>
                    {limiteEdit ? (
                        <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
                            <input type="number" value={limiteVal} onChange={e => setLimiteVal(e.target.value)}
                                style={{ ...inp, width: 130 }} autoFocus />
                            <button data-liquid-primary="true" onClick={guardarLimite} style={btnPri}>Guardar</button>
                            <button data-liquid-secondary="true" onClick={() => setLimiteEdit(false)} style={btnSec}>Cancelar</button>
                        </span>
                    ) : (
                        <span style={{ display: 'inline-flex', gap: 8, alignItems: 'center' }}>
                            <strong style={{ fontSize: '1rem' }}>{eur(limite)}</strong>
                            {isAdmin && (
                                <button data-liquid-secondary="true" onClick={() => { setLimiteVal(String(limite)); setLimiteEdit(true); }}
                                    style={{ ...btnSec, padding: '4px 10px' }}>Alterar</button>
                            )}
                        </span>
                    )}
                </div>
                <div style={{ marginLeft: 'auto', color: '#64748b', fontSize: '0.85rem' }}>
                    {candidatos.length} a avaliar · <strong style={{ color: pendentes ? '#b45309' : '#166534' }}>{pendentes} pendentes</strong>
                </div>
            </div>

            {loading ? (
                <p style={{ color: '#718096' }}>A carregar…</p>
            ) : erro ? (
                <p style={{ color: '#e53e3e' }}>{erro}</p>
            ) : candidatos.length === 0 ? (
                <div style={{ padding: '48px 0', textAlign: 'center', color: '#94a3b8' }}>
                    Nenhum fornecedor a avaliar em {ano}.<br />
                    <span style={{ fontSize: '0.85rem' }}>
                        São pré-selecionados os fornecedores com faturação acima de {eur(limite)} ou marcados como críticos.
                    </span>
                </div>
            ) : (
                <div className="table-responsive" style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflow: 'hidden' }}>
                    <table className="standard-table">
                        <thead>
                            <tr>
                                <th>Fornecedor</th>
                                <th style={{ textAlign: 'right' }}>Faturação {ano}</th>
                                <th>Motivo</th>
                                <th style={{ textAlign: 'center' }}>IQF</th>
                                <th>Classificação</th>
                                <th style={{ textAlign: 'right' }}>Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {candidatos.map(c => {
                                const mi = motivoInfo(c.motivo);
                                const ni = nivelInfo(c.nivel);
                                const avaliado = !!c.avaliacao_id;
                                return (
                                    <tr key={c.id}>
                                        <td style={{ fontWeight: 600 }}>
                                            {c.nome}
                                            {c.nif && <span style={{ color: '#94a3b8', fontWeight: 400 }}> · {c.nif}</span>}
                                        </td>
                                        <td style={{ textAlign: 'right' }}>{eur(c.total_ef)}</td>
                                        <td><span data-liquid-tone={liquidTone(mi.bg)} data-liquid-badge="true" style={{ ...badge, background: mi.bg, color: mi.fg }}><LiquidText>{mi.label}</LiquidText></span></td>
                                        <td style={{ textAlign: 'center', fontWeight: 700 }}>
                                            {c.iqf !== null && c.iqf !== undefined ? Number(c.iqf).toFixed(2) : '—'}
                                        </td>
                                        <td>
                                            {avaliado
                                                ? <span data-liquid-tone={liquidTone(ni.bg)} data-liquid-badge="true" style={{ ...badge, background: ni.bg, color: ni.fg }}><LiquidText>{ni.label}</LiquidText></span>
                                                : <span data-liquid-tone={liquidTone('#fff7ed')} data-liquid-badge="true" style={{ ...badge, background: '#fff7ed', color: '#c2410c' }}>Pendente</span>}
                                            {c.email_enviado_em && (
                                                <span title={`Comunicado em ${new Date(c.email_enviado_em).toLocaleString('pt-PT')}`}
                                                    style={{ marginLeft: 6, color: '#16a34a', fontSize: '0.8rem' }}><LiquidText>✉✓</LiquidText></span>
                                            )}
                                        </td>
                                        <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                            {avaliado && (
                                                <button data-liquid-secondary="true" onClick={() => setEmailModal(c)} style={{ ...btnSec, marginRight: 8 }}
                                                    title="Comunicar resultado ao fornecedor"><LiquidText>✉ Email</LiquidText></button>
                                            )}
                                            <button data-liquid-primary={(avaliado) ? undefined : true} data-liquid-secondary={(avaliado) ? true : undefined} onClick={() => setEditando(c)} style={avaliado ? btnSec : btnPri}>
                                                {avaliado ? 'Editar' : 'Avaliar'}
                                            </button>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            )}
            </>
            )}

            {editando && (
                <ModalAvaliacao
                    candidato={editando}
                    ano={ano}
                    onClose={() => setEditando(null)}
                    onSaved={carregar}
                />
            )}

            {emailModal && (
                <ModalEmail
                    candidato={emailModal}
                    onClose={() => setEmailModal(null)}
                    onSent={carregar}
                />
            )}
            </div>
        </div>
    );
};

/* ---------- estilos ---------- */
const lbl = { display: 'block', fontSize: '0.74rem', color: '#64748b', fontWeight: 600, marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { padding: '7px 10px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: '0.9rem' };
const badge = { padding: '3px 10px', borderRadius: 12, fontSize: '0.78rem', fontWeight: 600, whiteSpace: 'nowrap' };
const btnSec ={ padding: '7px 14px', border: '1px solid #cbd5e1', background: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem', color: '#334155' };
const btnPri = { padding: '7px 14px', border: '1px solid #3182ce', background: '#3182ce', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: '0.82rem' };
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', zIndex: 1000, padding: '40px 16px', overflowY: 'auto' };
const modal = { background: '#fff', borderRadius: 14, padding: 22, width: '100%', maxWidth: 640, boxShadow: '0 20px 60px rgba(0,0,0,0.25)' };
const closeBtn = { border: 'none', background: 'transparent', fontSize: '1.2rem', cursor: 'pointer', color: '#64748b' };

export default SistemaIntegrado;
